import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  onboardingApi,
  UserPreferencesSummary,
  CompleteOnboardingRequest,
  UpdatePreferencesRequest,
} from '../api/onboardingApi';
import {
  invalidatePreferencesCache,
  preferencesCache,
} from '../api/dashboardCaches';

interface PreferencesContextValue {
  preferences: UserPreferencesSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<UserPreferencesSummary | null>;
  completeOnboarding: (
    body: CompleteOnboardingRequest,
  ) => Promise<UserPreferencesSummary>;
  updatePreferences: (
    body: UpdatePreferencesRequest,
  ) => Promise<UserPreferencesSummary>;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(
  undefined,
);

// Single fetch + state instance shared by every consumer (ProtectedRoutes'
// onboarding gate and any component that writes preferences). Without this,
// each component's own usePreferences() call held an independent copy of
// `preferences`, so completing onboarding updated the writer's copy but left
// ProtectedRoutes' copy stale, bouncing the user back to /onboarding right
// after navigating to /dashboard. Writers outside the provider (the /start
// flow) must update `preferencesCache` for the same reason.
export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getToken } = useAuth();
  const cached = preferencesCache.peek();
  const [preferences, setPreferences] = useState<UserPreferencesSummary | null>(
    cached,
  );
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = useCallback(
    async (options: { force?: boolean } = {}) => {
      const warm = !options.force && preferencesCache.isFresh();
      if (!warm) setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');
        const data = await preferencesCache.get(
          () => onboardingApi.getPreferences(token),
          options,
        );
        setPreferences(data);
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch preferences';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  const completeOnboarding = useCallback(
    async (body: CompleteOnboardingRequest) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const data = await onboardingApi.completeOnboarding(token, body);
      preferencesCache.set(data);
      setPreferences(data);
      return data;
    },
    [getToken],
  );

  const updatePreferences = useCallback(
    async (body: UpdatePreferencesRequest) => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const data = await onboardingApi.updatePreferences(token, body);
      preferencesCache.set(data);
      setPreferences(data);
      return data;
    },
    [getToken],
  );

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return React.createElement(
    PreferencesContext.Provider,
    {
      value: {
        preferences,
        loading,
        error,
        refetch: () => {
          invalidatePreferencesCache();
          return fetchPreferences({ force: true });
        },
        completeOnboarding,
        updatePreferences,
      },
    },
    children,
  );
};

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return ctx;
}
